import type { ApplicationPurpose } from "@solomon-apps/shared/src/types/clientOnboardingPolicy";
/**
 * Client-facing onboarding API wrappers. The backend
 * (`~/yaas-api/src/api/onboarding/`) owns the state machine: one
 * `OnboardingApplication` per (clientOrg, issuerOrg) pair, nine
 * `OnboardingStep` rows per application (eight applicant-driven plus
 * `sanctions_screening` admin-driven). The BD provisioning flow
 * pre-creates the application so the FE just fetches and submits.
 *
 * Step submit endpoints all share the shape
 * `POST /org/client/onboarding/applications/:uuid/steps/<slug>/submit`
 * returning `{ application, step }`. We keep the per-step submitters
 * typed individually so the request payloads are checked at the call
 * site against the backend schemas in
 * `~/yaas-api/src/api/onboarding/stepPayloadSchemas/*.ts`.
 */

import { orgApiFetch, type PaginatedResponse } from "./orgApiFetch";

// ─── Enums (mirror backend strings) ─────────────────────────────────────────

/**
 * `awaiting_agreements` sits between review and approval: the issuer has
 * approved and the agreements are out for e-signature. The client console
 * stays closed through it — the DocuSign webhook moves the application to
 * `approved` once every required envelope is fully signed.
 */
export type OnboardingApplicationStatus = "draft" | "in_review" | "awaiting_agreements" | "approved" | "rejected";

export type OnboardingStepStatus = "not_started" | "in_progress" | "pending_external" | "complete" | "rejected";

export type OnboardingStepName =
    | "use_case_and_intent"
    | "legal_entity"
    | "tax_identification"
    | "signatory_authority"
    | "beneficial_ownership"
    | "sanctions_screening"
    | "source_of_funds"
    | "wallet_declarations"
    | "msa_signing"
    | "minting_agreement_signing";

// ISO 3166-1 alpha-2 country code, uppercase (e.g. "US", "GB"). The picker
// stores the country of incorporation directly; the backend column is free
// `text`. Legacy rows may still hold the old binary values "us" / "non_us".
export type IncorporationJurisdiction = string;

export type YaasUseCase = "treasury" | "liquidity_provider" | "protocol_owned_liquidity" | "neobank" | "other";

export type ExpectedCapitalRange = "under_500k" | "500k_to_2m" | "2m_to_10m" | "over_10m";

export type NeobankYieldDistribution = "pass_through" | "protocol_retains" | "protocol_distributes";

// WalletControlType removed in Phase C — control proof is no longer
// carried in the step payload; ParticipantAccount.walletSourceKind is the
// authority.

export type OnboardingEntryStatus = "not_started" | "pending_external" | "complete" | "rejected";

// ─── Views (mirror backend public schemas) ──────────────────────────────────

export interface OnboardingApplicationView extends ApplicationPurpose {
    uuid: string;
    clientOrganizationUuid: string;
    issuerOrganizationUuid: string;
    submittedByOrgUserUuid: string;
    status: OnboardingApplicationStatus;
    submittedAt: string | null;
    rejectionReason: string | null;
    signatoryEmail: string | null;
    signatoryName: string | null;
    incorporationJurisdiction: IncorporationJurisdiction | null;
    // Set the first time the client opens the post-approval welcome
    // celebration. `null` while `status === "approved"` means "first sign-in
    // since approval" — the `OnboardingGate` keys the one-time celebration off
    // this. Absent (older API) is treated as `null` by callers.
    approvalAcknowledgedAt: string | null;
    // When the applicant last accepted the Terms of Service on the lite
    // onboarding form (the lite submit stamps it). `null` for the full
    // flow, whose terms live in the agreements it signs. Optional: absent
    // on older API responses, which reads as never accepted.
    termsAcceptedAt?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface OnboardingStepView {
    uuid: string;
    applicationUuid: string;
    stepName: OnboardingStepName;
    status: OnboardingStepStatus;
    // The applicant's last-submitted payload for this step. The shape
    // varies by stepName; cast at the use-site against the matching
    // payload interface (e.g. `step.payload as LegalEntityPayload`).
    payload: Record<string, unknown> | null;
    submittedAt: string | null;
    completedAt: string | null;
    rejectedAt: string | null;
    rejectionReason: string | null;
    createdAt: string;
    updatedAt: string;
}

// ─── Agreements (DocuSign) ──────────────────────────────────────────────────

export type AgreementType = "msa" | "minting_master";

export type AgreementEnvelopeStatus =
    | "created"
    | "sent"
    | "delivered"
    | "client_signed"
    | "signed"
    | "declined"
    | "voided"
    | "email_bounced";

export interface AgreementEnvelopeView {
    uuid: string;
    applicationUuid: string;
    stepUuid: string;
    agreementType: AgreementType;
    status: AgreementEnvelopeStatus;
    sentAt: string | null;
    deliveredAt: string | null;
    clientSignedAt: string | null;
    issuerSignedAt: string | null;
    declinedAt: string | null;
    declinedReason: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ApplicationBundle {
    application: OnboardingApplicationView;
    steps: OnboardingStepView[];
    /** The latest envelope per agreement type the issuer sent. Absent on
     *  older API responses and empty before approval; the hub's agreement
     *  panels read their whole tracking state from here rather than polling
     *  a status endpoint per agreement. */
    agreements?: AgreementEnvelopeView[];
    /** Server-owned lite-onboarding flag (mirrors yaas-api
     *  `onboarding.liteEnabled`). `OnboardingEntry` branches `/onboarding`
     *  on this so the lite form only renders where the submit-lite endpoint
     *  is actually enabled. Optional: absent on older API responses, which
     *  falls back to the full flow. */
    liteOnboardingEnabled?: boolean;
    /** Server-owned mirror of yaas-api `onboarding.agreementsViaDocusign`.
     *  False means the agreements are handled outside the platform: the
     *  issuer's approval activates the client at once and no envelope is
     *  ever created, so nothing here should promise a signing email.
     *  Optional: absent on older API responses, which reads as sending. */
    agreementsViaDocusign?: boolean;
}

export interface StepSubmitResult {
    application: OnboardingApplicationView;
    step: OnboardingStepView;
}

// ─── Step payloads (mirror backend payload schemas) ─────────────────────────

export interface UseCaseAndIntentPayload {
    useCases: YaasUseCase[];
    expectedCapitalRange: ExpectedCapitalRange;
    yieldDistribution?: NeobankYieldDistribution | null;
    intentDescription?: string | null;
}

export interface LegalEntityPayload {
    legalName?: string | null;
    tradingName?: string | null;
    entityType?: string | null;
    registeredAddress?: string | null;
    companyNumber?: string | null;
    incorporationJurisdiction?: IncorporationJurisdiction | null;
    formationDocumentUuid?: string | null;
    operatingDocumentUuid?: string | null;
    certificateOfGoodStandingUuid?: string | null;
    primaryContactEmail?: string | null;
    primaryContactName?: string | null;
    primaryContactSameAsSignatory?: boolean;
    legalContactEmail?: string | null;
    legalContactName?: string | null;
}

export interface TaxIdentificationPayload {
    taxIdValue: string;
    taxIdJurisdiction: string;
    taxIdDocumentUuid?: string | null;
    fatcaClassification?: string | null;
}

export interface SignatoryAuthorityPayload {
    signatoryName: string;
    signatoryEmail: string;
    signatoryTitle: string;
    authorizationDocumentUuid?: string | null;
}

export interface BeneficialOwnershipEntry {
    entryUuid: string;
    legalName: string;
    ownershipPercent: number;
    governmentIdUuid: string;
    personaInquiryRef?: string | null;
    role?: string | null;
    entryStatus: OnboardingEntryStatus;
}

export interface BeneficialOwnershipPayload {
    entries: BeneficialOwnershipEntry[];
}

export interface SourceOfFundsPayload {
    sourceCategory: string;
    sourceDescription: string;
    supportingDocumentUuid?: string | null;
}

/**
 * One row of the lite Authorized Address Register (Order Form 2.3): an
 * address the client will mint from or redeem to, taken as declared. Lite
 * runs no wallet-verification ceremony. `primaryAssets` are asset symbols.
 */
export interface LiteWalletDeclaration {
    address: string;
    label?: string | null;
    primaryAssets: string[];
}

/** What a lite submission stores on the `wallet_declarations` step. */
export interface LiteDeclaredAddressesPayload {
    declaredAddresses: LiteWalletDeclaration[];
}

// Lite onboarding payload. Mirrors the backend `liteOnboardingPayloadSchema`:
// no external-verification fields, but everything the two agreements print
// (the Parties clause, the signatory) plus the declared mint/redeem
// addresses, the one document lite asks for (the formation document,
// uploaded ahead of the submit through `uploadOnboardingDocument`) and the
// Terms of Service acceptance. Submitting sends the application to manual
// issuer review; see `OnboardingLite.tsx`.
export interface LiteOnboardingPayload {
    companyName: string;
    legalName: string;
    /** Required for platform onboarding. Minting-only applications preserve
     * historical entity types but deliberately do not submit a new value. */
    entityType?: string;
    incorporationJurisdiction: IncorporationJurisdiction;
    registeredAddress: string;
    companyNumber: string;
    /** A `formation_document` upload of this application. */
    formationDocumentUuid: string;
    primaryContactEmail: string;
    primaryContactName?: string | null;
    primaryContactSameAsSignatory?: boolean;
    signatoryName: string;
    signatoryEmail: string;
    signatoryTitle: string;
    wallets: LiteWalletDeclaration[];
    useCases: YaasUseCase[];
    expectedCapitalRange?: ExpectedCapitalRange | null;
    /** Always `true` on the wire: the form does not submit without it, and
     *  the API refuses an explicit `false`. */
    termsAccepted: true;
}

// ─── Application lifecycle ──────────────────────────────────────────────────

const BASE = "/api/org/client/onboarding/applications";

/**
 * List the calling client's onboarding applications. The BD provisioning
 * flow pre-creates a single Draft application against the inviting issuer,
 * so first-time visitors get exactly one row here; multi-issuer support is
 * forward-compatible.
 */
export function listClientApplications(): Promise<PaginatedResponse<ApplicationBundle>> {
    return orgApiFetch<PaginatedResponse<ApplicationBundle>>(BASE);
}

export function getApplication(applicationUuid: string): Promise<ApplicationBundle> {
    return orgApiFetch<ApplicationBundle>(`${BASE}/${applicationUuid}`);
}

export function submitForReview(applicationUuid: string): Promise<OnboardingApplicationView> {
    return orgApiFetch<OnboardingApplicationView>(`${BASE}/${applicationUuid}/submit-for-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}"
    });
}

/** Response of {@link acknowledgeApproval}: the refreshed bundle (with
 *  `approvalAcknowledgedAt` now set) plus whether this call performed the
 *  first stamp (`false` when it was already acknowledged). */
export interface AcknowledgeApprovalResult {
    bundle: ApplicationBundle;
    acknowledgedNow: boolean;
}

/**
 * Record that the client has opened the post-approval welcome celebration.
 * Idempotent and Approved-only on the server — a repeat call returns the same
 * bundle with `acknowledgedNow: false`. The caller patches the applications
 * query cache from `bundle` so the `OnboardingGate` lets the client through on
 * the next evaluation (and never shows the celebration again).
 */
export function acknowledgeApproval(applicationUuid: string): Promise<AcknowledgeApprovalResult> {
    return orgApiFetch<AcknowledgeApprovalResult>(`${BASE}/${applicationUuid}/acknowledge-approval`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}"
    });
}

/**
 * Lite submit. Persists the identity + intent fields plus the agreement
 * fields and sends the application to manual issuer review. The issuer
 * approves it through the same review gate as a full application, which
 * then either sends the agreements (the application waits in
 * `awaiting_agreements`) or, with agreements handled outside the platform,
 * approves it outright. The
 * response carries the refreshed application + steps so the caller can
 * rehydrate. Only reachable when the server enables lite onboarding (yaas-api
 * `onboarding.liteEnabled`, surfaced as `liteOnboardingEnabled` on the
 * application bundle).
 */
export function submitLite(applicationUuid: string, payload: LiteOnboardingPayload): Promise<ApplicationBundle> {
    return orgApiFetch<ApplicationBundle>(`${BASE}/${applicationUuid}/submit-lite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
}

// ─── Per-step submit helpers ────────────────────────────────────────────────

function submitStep<P>(applicationUuid: string, routePath: string, payload: P): Promise<StepSubmitResult> {
    return orgApiFetch<StepSubmitResult>(`${BASE}/${applicationUuid}/steps/${routePath}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
    });
}

export function submitUseCaseAndIntent(
    applicationUuid: string,
    payload: UseCaseAndIntentPayload
): Promise<StepSubmitResult> {
    return submitStep(applicationUuid, "use-case-and-intent", payload);
}

export function submitLegalEntity(applicationUuid: string, payload: LegalEntityPayload): Promise<StepSubmitResult> {
    return submitStep(applicationUuid, "legal-entity", payload);
}

export function submitTaxIdentification(
    applicationUuid: string,
    payload: TaxIdentificationPayload
): Promise<StepSubmitResult> {
    return submitStep(applicationUuid, "tax-identification", payload);
}

export function submitSignatoryAuthority(
    applicationUuid: string,
    payload: SignatoryAuthorityPayload
): Promise<StepSubmitResult> {
    return submitStep(applicationUuid, "signatory-authority", payload);
}

export function submitBeneficialOwnership(
    applicationUuid: string,
    payload: BeneficialOwnershipPayload
): Promise<StepSubmitResult> {
    return submitStep(applicationUuid, "beneficial-ownership", payload);
}

export function submitSourceOfFunds(applicationUuid: string, payload: SourceOfFundsPayload): Promise<StepSubmitResult> {
    return submitStep(applicationUuid, "source-of-funds", payload);
}

// wallet_declarations has no FE submit helper. The step status flips
// to Complete inside the wallet-verification ceremony on the BE
// (`ClientWalletService.verifyAndPersistWallet`), and the public submit
// route was removed for the same reason.

/**
 * The latest envelope of one agreement type. The hub reads its tracking
 * state off `ApplicationBundle.agreements` instead, so this stays for the
 * one-off read: a support check, or a panel that has no bundle in hand.
 *
 * There is deliberately no send helper. Agreements are sent by the issuer's
 * approval, and resent from the issuer console; the client console never
 * initiates one.
 */
export function getAgreementStatus(
    applicationUuid: string,
    agreementType: AgreementType
): Promise<{ envelope: AgreementEnvelopeView | null }> {
    return orgApiFetch<{ envelope: AgreementEnvelopeView | null }>(
        `${BASE}/${applicationUuid}/agreements/${agreementType}/status`
    );
}
